import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import React, { useState } from "react";
import { t } from "i18next";
import {
  StripeElements,
  StripeError,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import { THubstripeCustomer } from "../../../shared/types/hubstripeCustomer.type";
import { LoadingButton } from "@mui/lab";
import { Hubstripe } from "../../../api/hubstripe";

type BillingPaymentMethodsFormProps = {
  customer: THubstripeCustomer;
  onSuccess?: () => void;
  onFailure?: () => void;
};

const BillingPaymentMethodsForm = ({
  customer,
  onSuccess,
  onFailure,
}: BillingPaymentMethodsFormProps): React.JSX.Element | null => {
  const stripe = useStripe();
  const elements: StripeElements | null = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!elements) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Validate form inputs
      const result: { error?: StripeError } | undefined =
        await elements.submit();
      if (result?.error) {
        setError(result.error.message || "Validation failed");
        setLoading(false); // Fix: Ensure loading releases on validation failure
        return;
      }

      // 2. Fetch SetupIntent client secret
      const responseClientSecret = await Hubstripe.getCreateSetupIntent(
        customer.id,
      );
      const clientSecret = responseClientSecret.data.sclient;

      // 3. Confirm card setup
      const { error: stripeError } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (stripeError) {
        setError(stripeError.message || "Setup failed");
        if (typeof onFailure === "function") onFailure();
      } else {
        // Fix: Allow frame cleanup before unmounting parent state
        setTimeout(() => {
          if (typeof onSuccess === "function") onSuccess();
        }, 100);
      }
    } catch (err: any) {
      setError(err?.message || "An unexpected error occurred");
      if (typeof onFailure === "function") onFailure();
    } finally {
      setLoading(false);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: "tabs",
  };

  return (
    <form
      id="payment-form"
      onSubmit={handleSubmit}
      style={{
        maxHeight: "calc(95dvh - 140px)", // 140px ≈ modal padding + margins + header
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <LoadingButton
        type="submit"
        loading={loading}
        sx={{ mt: 2 }}
        variant="contained"
        fullWidth
      >
        {t("save")}
      </LoadingButton>

      {error && (
        <div
          className="card-error"
          role="alert"
          style={{ color: "red", marginTop: "8px" }}
        >
          {error}
        </div>
      )}
    </form>
  );
};

export default BillingPaymentMethodsForm;
